import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { StringParam, useQueryParams } from 'use-query-params';

import { getIdentityVerificationURL } from 'apis/legitimizer/authorize/api';
import { getLatestKycProcess } from 'apis/legitimizer/kyc/api';
import { getErrorMessageKey } from 'apis/legitimizer/kyc/utils';
import { KycError, KycIdentificationOption } from 'apis/legitimizer/types';
import { fetchMerchants } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { useSessionStorage } from 'common/hooks';
import { Typography as T } from 'common/typography';
import PageLoadError from 'core/PageLoadError';

import IdentityVerificationOptions from './IdentityVerificationOptions';
import { fetchVenues } from '../../../apis/restaurant-api/merchant-admin/venue/api';
import { hasPermissions, usePermissions } from '../../../common/wolt-permissions';
import { CenteredRoot, Heading } from '../ComplianceContent';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 620px;
`;

const InfoText = styled(T.Body2)`
  text-align: center;
`;

const DisclaimerText = styled(T.Body3)`
  text-align: center;
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const getDefaultOption = (authOptions: KycIdentificationOption[]): KycIdentificationOption => authOptions[0];

const IdentityVerificationWrapper = () => {
  const permissions = usePermissions();
  const {
    data: merchants,
    isLoading: merchantsIsLoading,
    isError: merchantsIsError,
  } = useQuery(['/v1/merchant-admin/merchants'], () => fetchMerchants(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
  const {
    data: venues,
    isLoading: venuesIsLoading,
    isError: venuesIsError,
  } = useQuery(['/v1/merchant-admin/venues'], () => fetchVenues(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
  const {
    data: kycProcess = null,
    isLoading: kycProcessIsLoading,
    isError: kycProcessIsError,
  } = useQuery(['kyc/processes/latest'], () => getLatestKycProcess(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });

  const isError = merchantsIsError || kycProcessIsError || venuesIsError;
  const isLoading =
    merchantsIsLoading || kycProcessIsLoading || venuesIsLoading || !kycProcess || !merchants || !venues;

  if (isError) {
    return <PageLoadError />;
  }

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="extraLarge" />
      </SpinnerContainer>
    );
  }

  const isEmployee = hasPermissions(
    [
      { resource: 'restaurant', operations: ['r'] },
      { resource: 'merchant', operations: ['r'] },
    ],
    permissions,
  );

  const isLiveUser = merchants.length > 0 || venues.length > 0;

  return (
    <IdentityVerification
      authOptions={kycProcess.config.options}
      showSkip={isLiveUser || isEmployee}
      error={kycProcess.error}
    />
  );
};

type Props = {
  authOptions: KycIdentificationOption[];
  showSkip: boolean;
  error: KycError | null;
};

const IdentityVerification = ({ authOptions, showSkip, error }: Props) => {
  const [isLoading, setLoading] = useState(false);
  const [authOption, setAuthOption] = useState(getDefaultOption(authOptions));

  const [query] = useQueryParams({
    oidc_error: StringParam,
  });

  const { locale: currentLocale, formatMessage } = useIntl();

  const oidcAuthUrlMutation = useMutation(
    () =>
      getIdentityVerificationURL(authOption.url, currentLocale, authOption.provider, 'identity-verification/redirect'),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: url => {
        window.location.href = url;
      },
      onError: () => {
        setLoading(false);
      },
    },
  );

  const handleContinue = () => {
    oidcAuthUrlMutation.mutate();
  };

  const handleSelectAuthOption = (provider: string) => {
    const option = authOptions.find(o => o.provider === provider) || getDefaultOption(authOptions);
    setAuthOption(option);
  };

  // Using empty string as falsy value to avoid converting to bool since storage only stores strings
  const [, setSkipIDVerification] = useSessionStorage('skipIDVerification', '');

  const handleSkipKYC = () => {
    setSkipIDVerification('true');

    // Redirect user to root path using location to force a refresh. It's necessary so that top level components can read
    // from Storage using the hook.
    window.location.href = '/';
  };

  const hasErrors = oidcAuthUrlMutation.isError || !!query.oidc_error;

  return (
    <CenteredRoot>
      <Stack>
        <Heading>
          <FormattedMessage id="compliance.kyc.intro.title" />
        </Heading>
        <InfoText>
          <FormattedMessage id="compliance.kyc.intro.info" />
        </InfoText>
      </Stack>

      <Stack>
        <IdentityVerificationOptions
          options={authOptions}
          selectedOption={authOption}
          handleSelectAuthOption={handleSelectAuthOption}
        />
      </Stack>

      <Stack>
        {hasErrors && (
          <Alert variant="error" title={formatMessage({ id: 'compliance.kyc.errors.title' })}>
            <FormattedMessage id={getErrorMessageKey(error)} />
          </Alert>
        )}

        <Button size="large" variant="blue" disabled={isLoading} loading={isLoading} onClick={handleContinue}>
          <FormattedMessage id="compliance.kyc.intro.continue" />
        </Button>

        {showSkip && (
          <Button size="large" variant="lightBlue" disabled={isLoading} onClick={handleSkipKYC}>
            <FormattedMessage id="compliance.kyc.intro.skip" />
          </Button>
        )}

        <DisclaimerText>
          <FormattedMessage id="compliance.kyc.intro.disclaimer" />
        </DisclaimerText>
      </Stack>
    </CenteredRoot>
  );
};

export default IdentityVerificationWrapper;
